// WP13 Pattern Detail — decision-grade drill-down rendered EXCLUSIVELY from
// GET /api/wp12/patterns/{canonicalPatternId} (active WP11 package).
// First-read order: header → structural-state labels → authoritative
// Why-it-matters (WP5) → This week (WP10 Weekly Framing, verbatim) → score
// history. Deeper: structural detail, drivers, observed footprint, explain &
// verify, provenance. Render → filter → navigate → explain. Never
// recalculate → classify → infer.
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useWp12 } from "./useWp12";
import InfoTip from "./InfoTip";
import { MarketLayout } from "./MarketTree";
import { StateGrid } from "./StateGrid";
import { RegionMiniMap } from "./RegionMiniMap";
import { DeepInsightBadge } from "./DeepInsight";
import { StateChip, patternDisplayTitle, ZoneChip } from "./TopPatternsTable";
import { zoneColor } from "./LeadersChart";

const enumLabel = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase() : "—");
const has = (v) => (Array.isArray(v) ? v.length > 0 : typeof v === "string" ? v.trim().length > 0 : v != null);

const Section = ({ title, tip, tipId, children, testId }) => (
    <section className="space-y-2" data-testid={testId}>
        <h2 className="text-sm font-semibold inline-flex items-center gap-2">
            {title}
            {tip && <InfoTip label={`${title} explanation`} testId={tipId}>{tip}</InfoTip>}
        </h2>
        {children}
    </section>
);

const TIP = {
    trend: "Published structural direction for the respective observation horizon.",
    velocity: "Describes the pace of structural movement, not the absolute score level. A Pattern can remain on a rising trajectory while the pace of strengthening is cooling.",
    breadth: "Describes how broadly the observed activity is distributed across the underlying participation footprint. Contracting breadth can coexist with a strong or rising score.",
    persistence: "Published persistence of the structural state within the observation window.",
    score: "Current published structural score for this Pattern. Score history shows score level over time and is not the same measure as Velocity or Breadth.",
    role: "Published Pattern role from the upstream publisher. Core patterns are curated durable structural patterns; Watchlist patterns are monitored candidates. This is authoritative published state, not a frontend classification, and does not change the Pattern's score or reliability.",
    weeklyStory: "This Pattern is one of the selected stories in this week's published Executive Read. The framing shown is the published Weekly Story text, rendered verbatim — separate from the multi-week structural view below.",
    patternWeek: "Pattern-specific weekly update published upstream for this Pattern and week, shown verbatim. This is a separate product from the Weekly Executive Read story selection — a Pattern can have a weekly update without being a selected story.",
    movement: "Published structural qualifiers for the latest weekly movement — a separate structural-movement product, distinct from any weekly framing above.",
    chains: "Blockchains observed in the supporting evidence for this Pattern. Counts reflect appearances in supporting evidence and may overlap — one evidence item can reference several blockchains — so bars are ranked support counts, never shares of a whole.",
    orgs: "Organizations observed in the supporting evidence for this Pattern. Counts reflect appearances in supporting evidence and may overlap — they are never causal drivers, dominance or leadership.",
};

// ── Structural state table (all published horizons, verbatim) ────────────────
const StateTable = ({ trends }) => (
    <div className="card-flat overflow-x-auto">
        <table className="w-full text-left">
            <thead>
                <tr className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] border-b border-[hsl(var(--line))]">
                    <th className="px-4 py-2">Horizon</th>
                    <th className="px-2 py-2">Trend <InfoTip label="Trend" testId="wp13-tip-trend">{TIP.trend}</InfoTip></th>
                    <th className="px-2 py-2">Velocity <InfoTip label="Velocity" testId="wp13-tip-velocity">{TIP.velocity}</InfoTip></th>
                    <th className="px-2 py-2">Breadth <InfoTip label="Breadth" testId="wp13-tip-breadth">{TIP.breadth}</InfoTip></th>
                    <th className="px-2 py-2">Persistence <InfoTip label="Persistence" testId="wp13-tip-persistence">{TIP.persistence}</InfoTip></th>
                    <th className="px-2 py-2">Reliability</th>
                    <th className="px-2 py-2 pr-4">Claim eligibility</th>
                </tr>
            </thead>
            <tbody>
                {(trends || []).map((t) => (
                    <tr key={t.horizonWeeks} className="border-b border-[hsl(var(--line))] last:border-0" data-testid={`wp13-state-h${t.horizonWeeks}`}>
                        <td className="px-4 py-2 num text-[11px] font-medium whitespace-nowrap">{t.horizonWeeks}w
                            <span className="text-[9px] text-[hsl(var(--mute))] ml-1.5">{t.windowStartWeek} → {t.windowEndWeek}</span>
                        </td>
                        <td className="px-2 py-2"><StateChip value={t.primaryTrendClass} /></td>
                        <td className="px-2 py-2"><StateChip value={t.velocityState} /></td>
                        <td className="px-2 py-2"><StateChip value={t.actorBreadthTrendClass} /></td>
                        <td className="px-2 py-2"><StateChip value={t.persistenceState} /></td>
                        <td className="px-2 py-2 text-[10px] text-[hsl(var(--ink-soft))]">{enumLabel(t.scoreTrendReliability)}</td>
                        <td className="px-2 py-2 pr-4 text-[10px] text-[hsl(var(--ink-soft))]">{enumLabel(t.scoreClaimEligibility)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ── Published score history (verbatim points, missing weeks stay missing) ────
const ScoreHistory = ({ timeline, color }) => {
    const rows = (timeline || []).map((p) => ({
        week: p.analyticalWeek?.split("-")[1] || p.analyticalWeek,
        score: p.displaySignalScoreExact ?? null,
    }));
    if (rows.length < 2) return null;
    const observed = rows.map((r) => r.score).filter((v) => v != null);
    const lo = Math.max(0, Math.floor(Math.min(...observed) / 10) * 10);
    const ticks = Array.from({ length: 5 }, (_, i) => Math.round(lo + ((100 - lo) * i) / 4));
    return (
        <div className="card-flat p-4" data-testid="wp13-score-history">
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--line))" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 9, fill: "hsl(var(--mute))" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis domain={[lo, 100]} ticks={ticks} allowDataOverflow tick={{ fontSize: 9, fill: "hsl(var(--mute))" }} tickFormatter={(v) => Math.round(v)} tickLine={false} axisLine={false} width={36} />
                    <Tooltip
                        contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid hsl(var(--line))" }}
                        formatter={(v) => [v != null ? Number(v).toFixed(1) : "—", "Score"]}
                    />
                    <Line type="monotone" dataKey="score" stroke={color || "hsl(var(--accent))"} strokeWidth={1.5} dot={false} connectNulls={false} isAnimationActive={false} />
                </LineChart>
            </ResponsiveContainer>
            <p className="text-[9px] text-[hsl(var(--mute))] mt-1">Published weekly score history. Weeks without a published observation are not drawn — missing observation is not zero.</p>
        </div>
    );
};

// ── Latest structural movement — published qualifiers as labelled chips ──────
const qualifierParts = (q) => {
    const m = /^(ACTOR_BREADTH|SOURCE_BREADTH|ACTOR_GROUP)_(.+)$/.exec(q || "");
    if (m) {
        const labels = { ACTOR_BREADTH: "Actor breadth", SOURCE_BREADTH: "Source breadth", ACTOR_GROUP: "Actor group" };
        return { label: labels[m[1]], value: m[2] };
    }
    return { label: "Qualifier", value: q };
};

const LatestMovement = ({ movement }) => {
    const qualifiers = movement?.structuralQualifiers || [];
    if (!movement?.primaryMovementClass && qualifiers.length === 0) return null;
    return (
        <section className="space-y-2" data-testid="wp13-latest-movement">
            <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                Latest structural movement
                <InfoTip label="Latest structural movement" testId="wp13-tip-movement">{TIP.movement}</InfoTip>
            </h2>
            <div className="card-flat p-4 flex items-start gap-x-6 gap-y-2 flex-wrap">
                {movement?.primaryMovementClass && (
                    <div data-testid="wp13-movement-class">
                        <div className="text-[8.5px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Weekly movement</div>
                        <StateChip value={movement.primaryMovementClass} />
                    </div>
                )}
                {qualifiers.map((q) => {
                    const parts = qualifierParts(q);
                    return (
                        <div key={q} data-testid={`wp13-qualifier-${q}`}>
                            <div className="text-[8.5px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">{parts.label}</div>
                            <StateChip value={parts.value} title={`Published structural qualifier: ${enumLabel(q)}`} />
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// ── Pattern weekly updates — WP5 Pattern Weekly Framing history (verbatim) ───
// Separate product from the WP10 Weekly Story selection. Newest framing is
// prominent; earlier framings stay accessible as disclosures. Sorted by
// asOfYearWeek descending (publisher already resolved valid revisions).
const fval = (v) => (typeof v === "string" && v.trim() && v.trim() !== "None" ? v.trim() : null);

const FramingBody = ({ f }) => {
    const blocks = [
        ["Observation", fval(f.observation)],
        ["Interpretation", fval(f.interpretation)],
        ["Strategic implication", fval(f.strategicImplication)],
        ["Uncertainty", fval(f.uncertaintyStatement)],
    ].filter(([, v]) => v);
    const watchFor = Array.isArray(f.watchFor) ? f.watchFor.filter((w) => fval(w?.watchFor || w)) : [];
    const stakeholders = Array.isArray(f.stakeholderImplications) ? f.stakeholderImplications.filter((s) => fval(s?.implication)) : [];
    if (!blocks.length && !watchFor.length && !stakeholders.length) return null;
    return (
        <div className="mt-2 space-y-2.5">
            {blocks.map(([label, text]) => (
                <div key={label}>
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">{label}</div>
                    <p className="text-[11.5px] leading-relaxed">{text}</p>
                </div>
            ))}
            {watchFor.length > 0 && (
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Watch for</div>
                    <ul className="list-disc pl-4 space-y-1">
                        {watchFor.map((w, i) => <li key={i} className="text-[11.5px] leading-relaxed">{fval(w?.watchFor || w)}</li>)}
                    </ul>
                </div>
            )}
            {stakeholders.length > 0 && (
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Stakeholder implications</div>
                    <div className="space-y-1">
                        {stakeholders.map((s, i) => (
                            <div key={i} className="text-[11.5px] leading-relaxed">
                                <span className="font-medium">{s.stakeholderType}</span>
                                <span className="text-[hsl(var(--ink-soft))]"> — {s.implication}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const PatternWeeklyFramings = ({ framings, packageWeek, color }) => {
    const sorted = (framings || []).slice().sort((a, b) => String(b.asOfYearWeek || "").localeCompare(String(a.asOfYearWeek || "")));
    if (sorted.length === 0) return null;
    const [latest, ...older] = sorted;
    const heading = latest.asOfYearWeek === packageWeek
        ? `Pattern update · ${latest.asOfYearWeek}`
        : `Latest pattern update · ${latest.asOfYearWeek}`;
    return (
        <section className="card-flat p-4 border-l-4" style={{ borderLeftColor: color }} data-testid="wp13-pattern-weekly">
            <div className="eyebrow inline-flex items-center gap-2 flex-wrap" data-testid="wp13-pattern-weekly-heading">
                {heading}
                <InfoTip label="Pattern update" testId="wp13-tip-pattern-week">{TIP.patternWeek}</InfoTip>
            </div>
            {fval(latest.weeklyHeadline) && (
                <div className="text-[13px] font-semibold mt-1.5 leading-snug" data-testid="wp13-pattern-weekly-headline">{latest.weeklyHeadline}</div>
            )}
            {fval(latest.weeklySummary) && (
                <p className="text-[11.5px] leading-relaxed text-[hsl(var(--ink-soft))] mt-1.5" data-testid="wp13-pattern-weekly-summary">{latest.weeklySummary}</p>
            )}
            <details className="mt-2" data-testid="wp13-pattern-weekly-more">
                <summary className="text-[10px] text-[hsl(var(--accent-ink))] cursor-pointer select-none">Full update</summary>
                <FramingBody f={latest} />
            </details>
            {older.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[hsl(var(--line))]" data-testid="wp13-pattern-weekly-history">
                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1.5">Earlier pattern updates</div>
                    <div className="space-y-1.5">
                        {older.map((f) => (
                            <details key={f.weeklyFramingId || f.asOfYearWeek} data-testid={`wp13-pattern-weekly-${f.asOfYearWeek}`}>
                                <summary className="text-[11px] cursor-pointer select-none leading-snug">
                                    <span className="num text-[10px] text-[hsl(var(--mute))] mr-1.5">{f.asOfYearWeek}</span>
                                    <span className="font-medium">{fval(f.weeklyHeadline) || "Pattern update"}</span>
                                </summary>
                                <div className="pl-3 border-l border-[hsl(var(--line))] ml-1 mt-1.5 mb-2">
                                    {fval(f.weeklySummary) && <p className="text-[11.5px] leading-relaxed text-[hsl(var(--ink-soft))]">{f.weeklySummary}</p>}
                                    <FramingBody f={f} />
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

// ── Featured in this week's Executive Read — WP10 Weekly Story (verbatim) ────
const WeeklyStoryContext = ({ wc, color }) => {
    if (!wc) return null;
    const framing = wc.weeklyFramingState === "GENERATED" ? wc.weeklyFraming : null;
    if (framing) {
        const extras = [];
        if (has(framing.whyItMatters)) extras.push(["Why it matters this week", framing.whyItMatters]);
        return (
            <section className="card-flat p-4 border-l-4" style={{ borderLeftColor: color }} data-testid="wp13-weekly-story">
                <div className="eyebrow inline-flex items-center gap-2 flex-wrap">
                    Featured in this week's Executive Read
                    {wc.storyRole && <span className="normal-case tracking-normal">· {enumLabel(wc.storyRole)}</span>}
                    {wc.movementClass && <StateChip value={wc.movementClass} />}
                    <InfoTip label="Weekly story" testId="wp13-tip-weekly-story">{TIP.weeklyStory}</InfoTip>
                </div>
                <div className="text-[13px] font-semibold mt-1.5 leading-snug" data-testid="wp13-weekly-story-headline">{framing.headline}</div>
                {has(framing.summary) && (
                    <p className="text-[11.5px] leading-relaxed text-[hsl(var(--ink-soft))] mt-1.5" data-testid="wp13-weekly-story-summary">{framing.summary}</p>
                )}
                {(extras.length > 0 || has(framing.watchFor)) && (
                    <details className="mt-2" data-testid="wp13-weekly-story-more">
                        <summary className="text-[10px] text-[hsl(var(--accent-ink))] cursor-pointer select-none">More from this week's read</summary>
                        <div className="mt-1.5 space-y-2">
                            {extras.map(([label, text]) => (
                                <div key={label}>
                                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">{label}</div>
                                    <p className="text-[11.5px] leading-relaxed">{text}</p>
                                </div>
                            ))}
                            {has(framing.watchFor) && (
                                <div>
                                    <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Watch for</div>
                                    <ul className="list-disc pl-4 space-y-1">
                                        {framing.watchFor.map((w, i) => <li key={i} className="text-[11.5px] leading-relaxed">{w}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </details>
                )}
                <Link to="/market-v2/weekly" className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline mt-2 inline-block" data-testid="wp13-weekly-story-link">Read in Weekly →</Link>
            </section>
        );
    }
    // Selected Weekly Story with MISSING framing — honest restrained state, no invented text.
    return (
        <div className="card-flat px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="wp13-weekly-story-selected">
            <span className="text-[11px] font-medium">Featured in this week's Executive Read{wc.storyRole ? ` · ${enumLabel(wc.storyRole)}` : ""}</span>
            {wc.movementClass && <StateChip value={wc.movementClass} />}
            <Link to="/market-v2/weekly" className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline" data-testid="wp13-weekly-story-link">Read in Weekly →</Link>
        </div>
    );
};

// ── Ranked footprint bars (overlapping evidence-mention counts) ──────────────
const FootprintBars = ({ rows, testId }) => {
    const max = Math.max(...rows.map((r) => r.count || 0), 1);
    return (
        <div className="space-y-1.5" data-testid={testId}>
            {rows.map((r) => (
                <div key={r.label} className="flex items-center gap-2">
                    {r.to ? (
                        <Link to={r.to} className="text-[10px] w-36 truncate flex-shrink-0 hover:text-[hsl(var(--accent-ink))] hover:underline" title={r.label}>{r.label || "—"}</Link>
                    ) : (
                        <span className="text-[10px] w-36 truncate flex-shrink-0" title={r.label}>{r.label || "—"}</span>
                    )}
                    <div className="flex-1 h-2 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                        <div className="h-full rounded-sm bg-[hsl(var(--accent))]/60" style={{ width: `${Math.max(3, ((r.count || 0) / max) * 100)}%` }} />
                    </div>
                    <span className="num text-[9px] text-[hsl(var(--mute))] w-6 text-right">{r.count}</span>
                </div>
            ))}
        </div>
    );
};

export default function PatternCanonical() {
    // Route is patterns/:candidateId (shared with legacy redirect) — param carries the cpat_ id.
    const { candidateId: canonicalPatternId } = useParams();
    const { status, data, error } = useWp12(`patterns/${encodeURIComponent(canonicalPatternId)}`);
    const marketRes = useWp12("market");
    const [showAllOrgs, setShowAllOrgs] = useState(false);

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]" data-testid="wp13-pattern-loading">Loading…</div>;
    if (status === "error" || !data?.pattern) {
        return (
            <div className="p-8 space-y-2" data-testid="wp12-pattern-notfound">
                <p className="text-sm">Pattern not found in the active publication package{error ? ` (${error})` : ""}.</p>
                <Link to="/market-v2" className="text-xs text-[hsl(var(--accent-ink))] hover:underline">← Back to Overview</Link>
            </div>
        );
    }

    const p = data.pattern;
    const interp = p.interpretation && typeof p.interpretation === "object" && Object.keys(p.interpretation).length > 0 ? p.interpretation : null;
    const sup = p.supportSummary || {};
    const regions = data.regionLinkedActivity || [];
    const orgs = data.organizations || [];
    const evidence = data.evidence || [];
    const chains = (data.blockchainSupport?.chains || []);
    const h12 = (p.structuralTrends || []).find((t) => t.horizonWeeks === 12);
    const h4 = (p.structuralTrends || []).find((t) => t.horizonWeeks === 4);
    const color = zoneColor(data.marketZone?.presentationColorToken || p.marketZoneKey);
    const hasFootprint = orgs.length > 0 || chains.length > 0 || regions.length > 0;

    return (
        <MarketLayout market={marketRes.data} currentPattern={{ canonicalPatternId: p.canonicalPatternId, title: patternDisplayTitle(p), useCaseKey: p.useCaseKey }}>
        <div className="max-w-5xl space-y-8" data-testid="wp12-pattern-canonical">
            {/* ── Header — parent Market Zone identity accent ─────────────────── */}
            <div className="flex items-start justify-between gap-6 flex-wrap border-l-4 pl-4" style={{ borderColor: color }}>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <ZoneChip zone={data.marketZone || { scopeKey: p.marketZoneKey, label: enumLabel(p.marketZoneKey) }} zoneKey={p.marketZoneKey} />
                        <Link to={`/market-v2/market/use-cases/${encodeURIComponent(p.useCaseKey || "")}`}
                              className="text-[10px] text-[hsl(var(--mute))] hover:text-[hsl(var(--accent-ink))] hover:underline"
                              data-testid="wp13-pattern-usecase-link">
                            {p.useCaseLabel || enumLabel(p.useCaseKey)}
                        </Link>
                    </div>
                    <h1 className="text-xl font-semibold mt-2 leading-snug" data-testid="wp13-pattern-title">{patternDisplayTitle(p)}</h1>
                    {p.displayTitle && p.patternLabel && p.displayTitle !== p.patternLabel && (
                        <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5" data-testid="wp12-pattern-canonical-label">{p.patternLabel}</div>
                    )}
                    {interp && <div className="mt-1.5"><DeepInsightBadge testId="wp13-deep-insight-header" /></div>}
                </div>
                <div className="text-right flex-shrink-0" data-testid="wp13-pattern-score">
                    <div className="eyebrow inline-flex items-center gap-1.5 justify-end">Current score <InfoTip label="Score" testId="wp13-tip-score">{TIP.score}</InfoTip></div>
                    <div className="num text-2xl font-semibold leading-none mt-1">{p.strength?.displayScoreExact != null ? p.strength.displayScoreExact.toFixed(1) : "—"}</div>
                    <div className="text-[10px] text-[hsl(var(--mute))] mt-0.5 inline-flex items-center gap-1 justify-end">
                        {enumLabel(p.strength?.scoreBand)}
                        {p.patternRole && (
                            <span data-testid="wp13-pattern-role" className="inline-flex items-center gap-1">
                                · {enumLabel(p.patternRole)}
                                <InfoTip label="Pattern role" testId="wp13-tip-role">{TIP.role}</InfoTip>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Compact structural-state labels ────────────────────────────── */}
            <div className="card-flat p-4" data-testid="wp13-at-a-glance">
                <StateGrid items={[
                    { label: "12w trend", value: h12?.primaryTrendClass },
                    { label: "4w trend", value: h4?.primaryTrendClass },
                    { label: "Velocity", value: h12?.velocityState },
                    { label: "Breadth", value: h12?.actorBreadthTrendClass },
                    { label: "Persistence", value: h12?.persistenceState },
                    { label: "Reliability", value: h12?.scoreTrendReliability, plain: true },
                    { label: "Claim eligibility", value: h12?.scoreClaimEligibility, plain: true },
                ]} />
            </div>

            {/* ── About this pattern — authoritative canonical description ───── */}
            {interp && has(interp.canonicalDescription) && (
                <div className="card-flat p-5" data-testid="wp13-about-pattern">
                    <div className="eyebrow" style={{ color }}>About this pattern</div>
                    <p className="text-[12.5px] leading-relaxed mt-1.5">{interp.canonicalDescription}</p>
                </div>
            )}

            {/* ── Pattern weekly updates (WP5 framing history, verbatim) ─────── */}
            <PatternWeeklyFramings framings={p.patternWeeklyFramings} packageWeek={data.packageWeek} color={color} />

            {/* ── WP10 Weekly Story context — separate product, kept distinct ── */}
            <WeeklyStoryContext wc={data.weeklyContext} color={color} />

            {/* ── Score history ──────────────────────────────────────────────── */}
            <Section title="Score history" testId="wp13-score-section">
                <ScoreHistory timeline={p.scoreTimeline} color={color} />
            </Section>

            {/* ── Why this pattern matters — authoritative text, verbatim ────── */}
            {interp && has(interp.whyItMatters) && (
                <div className="card-flat p-5" data-testid="wp13-why-main">
                    <div className="eyebrow" style={{ color }}>Why this pattern matters</div>
                    <p className="text-[12.5px] leading-relaxed mt-1.5">{interp.whyItMatters}</p>
                </div>
            )}

            {/* ── Latest structural movement (WP8 — distinct from This week) ─── */}
            <LatestMovement movement={p.latestMovement} />

            <details className="card-flat" data-testid="wp13-structural-state">
                <summary className="px-5 py-3 text-sm font-semibold cursor-pointer select-none inline-flex items-center gap-2 w-full">
                    All horizons — structural state detail
                    <InfoTip label="Horizons" testId="wp13-tip-state">Published states per observation horizon. Score trajectory, velocity and breadth measure different dimensions — a rising trajectory can coexist with cooling velocity or contracting breadth.</InfoTip>
                </summary>
                <div className="border-t border-[hsl(var(--line))]">
                    <StateTable trends={p.structuralTrends} />
                </div>
            </details>

            {/* ── Structural drivers — authoritative WP5 driver content only ─── */}
            {interp && has(interp.patternDrivers) && (
                <Section title="Structural drivers" testId="wp13-structural-drivers"
                         tip="Authoritative published driver interpretation for this Pattern. A weighted driver distribution is not published in the active package — nothing is recalculated to compensate."
                         tipId="wp13-tip-drivers">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wp13-drivers">
                        {interp.patternDrivers.map((d, i) => (
                            <div key={i} className="card-flat p-4">
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                    <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))]">{enumLabel(d.driverCategory)}</span>
                                    {(d.driverValues || []).map((v) => (
                                        <span key={v} className="px-1.5 py-0.5 rounded bg-[hsl(var(--surface-sunken))] text-[10px] font-medium">{v}</span>
                                    ))}
                                </div>
                                <p className="text-[11px] leading-relaxed text-[hsl(var(--ink-soft))]">{d.interpretation}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* ── Observed footprint — never causal drivers ──────────────────── */}
            {hasFootprint && (
                <Section title="Observed footprint" testId="wp13-observed-footprint"
                         tip="Organizations, blockchains and regions observed in the published supporting evidence for this Pattern. These are observational associations — overlapping support/evidence counts, never causal drivers, dominance, leadership or shares of a whole."
                         tipId="wp13-tip-footprint">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                        {orgs.length > 0 && (
                            <div className="card-flat p-4" data-testid="wp13-footprint-orgs">
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-2 inline-flex items-center gap-1.5">
                                    Organizations observed in supporting evidence
                                    <InfoTip label="Organization footprint" testId="wp13-tip-footprint-orgs">{TIP.orgs}</InfoTip>
                                </div>
                                <FootprintBars testId="wp13-footprint-orgs-bars"
                                               rows={orgs.slice(0, 8).map((o) => ({ label: o.displayName, count: o.evidenceCount || 0 }))} />
                            </div>
                        )}
                        {chains.length > 0 && (
                            <div className="card-flat p-4" data-testid="wp13-footprint-chains">
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-2 inline-flex items-center gap-1.5">
                                    Blockchains observed in supporting evidence
                                    <InfoTip label="Blockchain footprint" testId="wp13-tip-footprint-chains">{TIP.chains}</InfoTip>
                                </div>
                                <FootprintBars testId="wp13-footprint-chains-bars"
                                               rows={chains.slice(0, 8).map((c) => ({ label: c.blockchain, count: c.evidenceCount || 0 }))} />
                            </div>
                        )}
                        {regions.length > 0 && (
                            <div className="card-flat p-3" data-testid="wp13-geography">
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1 px-1 inline-flex items-center gap-1.5">
                                    Region-linked observed activity
                                    <InfoTip label="Pattern geography" testId="wp13-tip-geo">Published association between this Pattern and regions where supporting activity was observed — presence only. No weighted regional composition is published for Patterns in the active package, so the map highlights associated regions in the parent Market Zone identity color without intensity. Never a regional score.</InfoTip>
                                </div>
                                <RegionMiniMap geoKeys={regions.map((r) => r.geoKey)} color={color} height={150} />
                                <div className="text-[9px] text-[hsl(var(--mute))] px-1 mt-0.5">{regions.map((r) => r.label).join(" · ")}</div>
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* ── Explain & verify — deeper interpretation layer ─────────────── */}
            {interp ? (
                <div className="space-y-6" data-testid="wp13-interpretation">
                    {(has(interp.structuralInterpretation) || has(interp.scopeSummary)) && (
                        <Section title="What this pattern means" testId="wp13-what-it-means">
                            <div className="card-flat p-5 space-y-4">
                                {has(interp.structuralInterpretation) && (
                                    <div data-testid="wp13-structural-interpretation">
                                        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Structural interpretation</div>
                                        <p className="text-[12.5px] leading-relaxed">{interp.structuralInterpretation}</p>
                                    </div>
                                )}
                                {has(interp.scopeSummary) && (
                                    <div data-testid="wp13-scope">
                                        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Scope</div>
                                        <p className="text-[12.5px] leading-relaxed">{interp.scopeSummary}</p>
                                    </div>
                                )}
                            </div>
                        </Section>
                    )}

                    {has(interp.stakeholderImplications) && (
                        <Section title="Stakeholder implications" testId="wp13-stakeholders">
                            <div className="card-flat p-5 space-y-2">
                                {interp.stakeholderImplications.map((s, i) => (
                                    <div key={i} className="text-[11.5px] leading-relaxed">
                                        <span className="font-medium">{s.stakeholderType}</span>
                                        <span className="text-[hsl(var(--ink-soft))]"> — {s.implication}</span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    {has(interp.watchpoints) && (
                        <Section title="What to watch" testId="wp13-watchpoints">
                            <ul className="card-flat p-5 space-y-2.5 list-disc pl-9">
                                {interp.watchpoints.map((w, i) => (
                                    <li key={i} className="text-[12px] leading-relaxed">{w.watchpoint}</li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {(has(interp.exclusions) || has(interp.uncertainty) || has(interp.structuralRelationships)) && (
                        <Section title="Limits & uncertainty" testId="wp13-limits">
                            <div className="card-flat p-5 space-y-3">
                                {has(interp.exclusions) && (
                                    <div>
                                        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Out of scope</div>
                                        <ul className="space-y-1">
                                            {interp.exclusions.map((x, i) => <li key={i} className="text-[11.5px] leading-relaxed">{x.statement}</li>)}
                                        </ul>
                                    </div>
                                )}
                                {has(interp.uncertainty) && (
                                    <div>
                                        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Uncertainty</div>
                                        <p className="text-[11.5px] leading-relaxed">{interp.uncertainty}</p>
                                    </div>
                                )}
                                {has(interp.structuralRelationships) && (
                                    <div>
                                        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Structural relationships</div>
                                        <ul className="space-y-1">
                                            {interp.structuralRelationships.map((r, i) => <li key={i} className="text-[11.5px] leading-relaxed">{typeof r === "string" ? r : r.statement || JSON.stringify(r)}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </Section>
                    )}
                </div>
            ) : (
                <p className="text-[11px] text-[hsl(var(--mute))] border-l-2 border-[hsl(var(--line))] pl-3" data-testid="wp13-no-interpretation">
                    An editorial structural interpretation is not currently available for this Pattern.
                </p>
            )}

            {/* ── Organization footprint detail ──────────────────────────────── */}
            {orgs.length > 0 && (
                <Section title="Organizations observed in supporting evidence" testId="wp13-organizations"
                         tip="Observational association from published supporting evidence — not organization domicile, market leadership or company-type truth."
                         tipId="wp13-tip-orgs">
                    <div className="card-flat overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] border-b border-[hsl(var(--line))]">
                                    <th className="px-4 py-2">Organization</th>
                                    <th className="px-2 py-2 text-right">Evidence</th>
                                    <th className="px-2 py-2 text-right">Sources</th>
                                    <th className="px-2 py-2 text-right">Weeks observed</th>
                                    <th className="px-2 py-2 pr-4">Observed window</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orgs.slice(0, showAllOrgs ? orgs.length : 5).map((o) => (
                                    <tr key={o.canonicalOrganizationId} className="border-b border-[hsl(var(--line))] last:border-0">
                                        <td className="px-4 py-2 text-[11.5px] font-medium">
                                            {o.canonicalOrganizationId ? (
                                                <Link to={`/market-v2/organizations/${encodeURIComponent(o.canonicalOrganizationId)}`}
                                                      className="hover:text-[hsl(var(--accent-ink))] hover:underline"
                                                      data-testid={`wp13-org-link-${o.canonicalOrganizationId}`}>
                                                    {o.displayName || "—"}
                                                </Link>
                                            ) : (o.displayName || "—")}
                                        </td>
                                        <td className="px-2 py-2 num text-[10px] text-right">{o.evidenceCount ?? "—"}</td>
                                        <td className="px-2 py-2 num text-[10px] text-right">{o.sourceCount ?? "—"}</td>
                                        <td className="px-2 py-2 num text-[10px] text-right">{o.weeksObserved ?? "—"}</td>
                                        <td className="px-2 py-2 pr-4 num text-[10px] text-[hsl(var(--mute))]">{o.firstSeenWeek} → {o.lastSeenWeek}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {orgs.length > 5 && (
                        <button type="button" onClick={() => setShowAllOrgs((v) => !v)} data-testid="wp13-orgs-toggle"
                                className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline">
                            {showAllOrgs ? "Show top 5" : `View all ${orgs.length}`}
                        </button>
                    )}
                </Section>
            )}

            {/* ── Evidence & provenance ──────────────────────────────────────── */}
            <Section title="Evidence & provenance" testId="wp13-evidence"
                     tip="Published supporting evidence resolved by publisher IDs. Evidence volume reflects observation coverage, not structural strength."
                     tipId="wp13-tip-evidence">
                <div className="card-flat p-4 text-[11px] flex items-baseline gap-x-5 gap-y-1 flex-wrap" data-testid="wp13-support-summary">
                    <span>Observed since <span className="num font-medium">{sup.firstSeenWeek || "—"}</span></span>
                    <span><span className="num font-medium">{sup.weeksObserved ?? "—"}</span> observed weeks</span>
                    <span>Last observed <span className="num font-medium">{sup.latestObservedWeek || sup.lastSeenWeek || "—"}</span></span>
                    {sup.currentWeekCoverageStatus === "CONFIRMED_ZERO" && (
                        <span className="text-[hsl(var(--mute))]">No confirmed activity in the current week</span>
                    )}
                    <span className="text-[hsl(var(--mute))]">{data.evidenceTotal ?? 0} published evidence records reference this pattern</span>
                </div>
                {evidence.length > 0 && (
                    <div className="space-y-2" data-testid="wp13-evidence-list">
                        {evidence.slice(0, 8).map((e) => (
                            <div key={e.evidenceId} className="card-flat p-3.5">
                                <div className="flex items-baseline justify-between gap-3">
                                    <a href={e.sourceUrl} target="_blank" rel="noreferrer"
                                       className="text-[11.5px] font-medium leading-snug hover:text-[hsl(var(--accent-ink))] hover:underline min-w-0"
                                       data-testid={`wp13-evidence-${e.evidenceId}`}>
                                        {e.displayTitle || e.sourceDomain}
                                    </a>
                                    <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{e.evidenceDate}</span>
                                </div>
                                <div className="text-[9px] text-[hsl(var(--mute))] mt-0.5">
                                    {e.sourceDomain}{e.newsEventType ? ` · ${e.newsEventType}` : ""}{e.contentType ? ` · ${enumLabel(e.contentType)}` : ""}
                                </div>
                                {e.summary && <p className="text-[10.5px] text-[hsl(var(--ink-soft))] leading-snug mt-1 line-clamp-2">{e.summary}</p>}
                            </div>
                        ))}
                    </div>
                )}
                <p className="text-[9px] text-[hsl(var(--mute))]">Evidence volume reflects observation coverage, not structural strength.</p>
            </Section>

            <Link to="/market-v2" className="text-xs text-[hsl(var(--accent-ink))] hover:underline inline-block">← Back to Overview</Link>
        </div>
        </MarketLayout>
    );
}
